import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Container, Row, Col, Button } from "reactstrap";
import { GetEnquiryDetail } from "../../api/enquiryService";
import { format, parseISO } from "date-fns";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import PriceValue from "../../components/Common/PriceValue";
import FullScreenLoader from "../../components/Common/Loader";

const DEFAULT_IMAGE = "/assets/images/noImage/no-image3.png";

const EnquiryDetail = () => {
	document.title = "Enquiry Detail";

    const [showLoader, setShowLoader] = useState(false);
    const { ENQUIRY_ID } = useParams();
	const navigate = useNavigate();
    const [progress, setProgress] = useState(0);
    const [dataReady, setDataReady] = useState(false);
	const [enquiryItems, setEnquiryItems] = useState([]);
    const [loadedImages, setLoadedImages] = useState({});
    const [errorMessage, setErrorMessage] = useState(""); 
	const isInvalid = (val) => val === null || val === undefined || val === "" || Number(val) === 0;
	const formatPcsCts = (pcs, wt) => {
		return isInvalid(pcs) || isInvalid(wt) ? "-" : `${pcs} Pcs / ${wt} Cts`;
	};

	const formatDateKeepTime = (dateTime) => {
		if (!dateTime) return "-";

		const date = parseISO(dateTime);
		return format(date, "dd MMM yyyy hh:mm a");
	};

	const showVal = (val) => val === 0 || val === "0" || val === null || val === undefined ? "-" : val;

	useEffect(() => {
		fetchEnquiryDetail();
    }, [ENQUIRY_ID]);

    const fetchEnquiryDetail = async () => {
        if (!ENQUIRY_ID) return;
        setShowLoader(true);
        setDataReady(false);      
        try {
            const payload = {
                Enquiry_ID: ENQUIRY_ID,
            };

            const response = await GetEnquiryDetail(payload);

            if (response?.ZMESSAGE?.[0]?.RETURNCODE === "1") {
                setEnquiryItems(response?.EnquiryDetail || []);
            } else {
                setEnquiryItems([]);
                console.log("No data returned or error", response);
            }
            if (response?.ZMESSAGE?.[0]?.RETURNCODE !== "1") {
                setErrorMessage(response?.ZMESSAGE?.[0]?.RETURNMESSAGE || "Something went wrong.");
                return;
            }
        } catch (error) {
            console.error("Error fetching enquiry detail:", error);
            setEnquiryItems([]);
        } finally {
            setProgress(100);
            setTimeout(() => {
                setShowLoader(false);
                setDataReady(true);
            }, 400);
        }
    };

    useEffect(() => {
        if (!showLoader) return;
        let value = 10;
        setProgress(value);
        const interval = setInterval(() => {
            value = Math.min(value + Math.random() * 15, 90);
            setProgress(value);
        }, 400);
        return () => clearInterval(interval);
    }, [showLoader]);

    const getStatusClass = (status = "") => status.toLowerCase().replace(/[^a-z0-9]+/g, "-");

	const handleImageLoad = (id) => {
		setLoadedImages(prev => ({ ...prev, [id]: true }));
	};

	const listVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				staggerChildren: 0.08,
			},
		},
	};

	const itemVariants = {
		hidden: { opacity: 0, y: 18 },
		visible: {
			opacity: 1,
			y: 0,
			transition: {
				duration: 0.45,
				ease: "easeOut",
			},
		},
		exit: {
			opacity: 0,
			y: -12,
			transition: { duration: 0.25 },
		},
	};

	return (
        <Container className="cart-container py-3">
            {showLoader && <FullScreenLoader progress={progress} />}
			<div className="cart-header-section">			
                <div className="cart-header-text">
                    <h2 className="fw-bold text-primary">Enquiry Detail: {enquiryItems[0]?.EnquiryNo || "-"}</h2>
                    <p>({enquiryItems.length})</p>
                </div>
				<div className="p-2">
					<Button color="primary" onClick={() => navigate(-1)} className="back-button">
						<i className="bx bx-left-arrow-alt"></i> Back
					</Button>
				</div>
			</div>
			{enquiryItems.length === 0 && (
                <div className="enquiry-empty-wrapper">
                    <div className="enquiry-empty-state text-center">
                        <img
                            src="/assets/images/enquiry/noEnquiry.png"
                            alt="Error"
                            className="enquiry-empty-image mb-3"
                            style={{ maxWidth: "150px" }}
                        />
                        <h5 className="fw-semibold mb-2">NO ENQUIRY DETAIL</h5>
                        <p className="text-muted mb-3">{errorMessage}</p>
                    </div>
                </div>
			)}
            <AnimatePresence>
                {enquiryItems.length > 0 && (
                    <div className="enquiry-header-wrapper">
                        <Row className="enquiry-detail-header">
                            <div className="enquiry-header-top">
                                <span className="text-muted enquiry-date"> {formatDateKeepTime(enquiryItems[0].EnquiryDate)} </span>
                                <p className="fw-bold enquiry-remark">
                                    Remark:
                                    <span className="fw-normal">
                                        {" "}
                                        {enquiryItems[0].Remark || "-"}
                                    </span>
                                </p>
                                <span className={`status-badge status-${getStatusClass(enquiryItems[0].Status)}`}>
                                    {enquiryItems[0].Status}
                                </span>
                            </div>
                        </Row>
                         <hr />
                    </div>
                )}

                <motion.div
                    variants={listVariants}
                    initial="hidden"
                    animate="visible"
                >
                    {enquiryItems.map((item, index) => (
                        <motion.div
                            key={item.Style_ID || index}
                            className="cart-item"
                            variants={itemVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                            layout
                        >
                            <div className="enquiry-body-wrapper">
                                <Row className="flex-lg-row">
                                    <Col xs="12" sm="4" md="4" lg="2" className="cart-image-col">
                                        <div className="image-wrapper">
                                            {!loadedImages[item.Style_ID] && <div className="image-skeleton" />}
                                            <img
                                                src={item.Image1 || DEFAULT_IMAGE}
                                                alt={item.StyleNo}
                                                className={`cart-image ${loadedImages[item.Style_ID] ? "visible" : ""}`}
                                                onLoad={() => handleImageLoad(item.Style_ID)}
                                                onError={(e) => {
                                                    e.target.src = DEFAULT_IMAGE;
                                                    handleImageLoad(item.Style_ID);
                                                }}
                                            />
                                        </div>
                                    </Col>
                                    <Col xs="12" sm="9" md="8">
                                        <div className="cart-title mb-1 row align-items-start">
                                            <span className="text-primary cart-desc col-12 col-md-6">
                                                {item.ShortDescription || "Product Detail"}
                                            </span>

                                        </div>
                                        <div className="d-flex align-items-baseline row mb-1">
                                            <span className="fw-bold me-1 col-12 col-md-6">
                                                Product Code: {" "}
                                                <a
                                                    href={`/vault-access-detail/${item.Style_ID}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="clickable-code login-hover-link fw-normal"
                                                >
                                                    {showVal(item.StyleNo)} / {showVal(item.JewelCode)}
                                                </a>
                                            </span>
                                            <span className="col-12 col-md-4 mt-1 mt-md-0">
                                                <PriceValue
                                                    lineAmount={item.LineAmount}
                                                    lineDiscount={item.LineDiscount}
                                                    priceRemark={item.PriceRemark}
                                                />
                                            </span>
                                        </div>

                                        <Row className="cart-details-row">
                                            <Col md="6">
                                                <span className="text-primary cart-subtitle mt-1">	<i className="fa fa-box-open fs-6 me-2"></i>Product Details: </span>                                           
                                                <p className="fw-bold">Gold Purity:
                                                    <span className="fw-normal"> {showVal(item.MetalQuality)} / {showVal(item.MetalTone)}</span>
                                                </p>
                                                <p className="fw-bold">Gold Weight:
                                                    <span className="fw-normal"> {showVal(item.TotalNetWt)}</span>
                                                </p>
                                                <p className="fw-bold">Dimension:
                                                    <span className="fw-normal"> {showVal(item.ItemSize)}</span>
                                                </p>
                                            </Col>

                                            <Col md="6">
                                                <span className="text-primary cart-subtitle"><i className="fa fa-gem fs-6 me-2"></i>Diamond Details:</span>
                                                <p className="fw-bold">Diamond Weight:
                                                    <span className="fw-normal"> {formatPcsCts(item.DiaPcs, item.DiaWt)}</span>
                                                </p>
                                                <p className="fw-bold">Color Diamond:
                                                    <span className="fw-normal"> {formatPcsCts(item.CDiaPcs, item.CDiaWt)}</span>
                                                </p>
                                                <p className="fw-bold">Gross Weight:
                                                    <span className="fw-normal"> {isInvalid(item.GrossWt) ? "-" : item.GrossWt}</span>
                                                </p>
                                            </Col>
                                        </Row>

                                    </Col>
                                </Row>
                            </div>
                        </motion.div>
                    ))}
                </motion.div>
            </AnimatePresence>
		</Container>
	);
};
export default EnquiryDetail;
